/**
 * Present supervisor thinking-step observability without implying false execution.
 * Planned/assigned routes are labels; executed_route is what actually ran.
 * Enterprise Direct LLM fallback is a separate kind from deep-task direct_llm labels.
 */

function splitDetailLines(detail) {
  return String(detail || '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
}

export function formatThinkingStepLines(step = {}) {
  const detail = String(step.detail || '');
  // Prefer backend multi-line detail (already truthful Planned/Executed/Result text).
  if (detail.includes('\n')) {
    return splitDetailLines(detail);
  }

  const obs = step && typeof step.observability === 'object' && step.observability
    ? step.observability
    : null;
  if (obs && obs.kind === 'deep_task') {
    const lines = [];
    if (obs.assigned_route) {
      lines.push(`Planned route: ${String(obs.assigned_route).toUpperCase()}`);
    }
    if (obs.executed_route) {
      lines.push(`Executed route: ${String(obs.executed_route).toUpperCase()}`);
    }
    if (Array.isArray(obs.attempted_routes) && obs.attempted_routes.length > 1) {
      lines.push(
        `Attempted: ${obs.attempted_routes.map((r) => String(r).toUpperCase()).join(' → ')}`,
      );
      const recovery = obs.attempted_routes.slice(1);
      if (recovery.length) {
        lines.push(
          `Recovery: ${recovery.map((r) => String(r).toUpperCase()).join(' → ')}`,
        );
      }
    }
    if (obs.result_label || obs.result_type) {
      lines.push(`Result: ${obs.result_label || obs.result_type}`);
    }
    return lines;
  }
  if (obs && obs.kind === 'enterprise_direct_llm_fallback') {
    const lines = [];
    if (obs.status_label) lines.push(`Status: ${obs.status_label}`);
    if (obs.context_sufficient === true) lines.push('Context: Sufficient');
    if (obs.context_sufficient === false) lines.push('Context: Insufficient');
    if (obs.grounded === true) lines.push('Grounding: Accepted');
    if (obs.grounded === false) lines.push('Grounding: Rejected');
    if (obs.reason_label) lines.push(`Reason: ${obs.reason_label}`);
    else if (obs.reason) lines.push(`Reason: ${obs.reason}`);
    return lines;
  }
  if (obs && obs.kind === 'summary_gated_direct_llm') {
    const lines = [];
    if (obs.status_label) lines.push(`Status: ${obs.status_label}`);
    lines.push('Mode: Summary-gated Direct LLM');
    if (obs.summary_relevant === true) lines.push('Summary relevant: Yes');
    if (obs.summary_relevant === false) lines.push('Summary relevant: No');
    if (obs.chunks_retrieved != null && obs.chunks_retrieved !== undefined) {
      lines.push(`Chunks retrieved: ${obs.chunks_retrieved}`);
    }
    if (obs.executed === true) lines.push('Direct LLM: invoked');
    if (obs.executed === false && obs.attempted === false) {
      lines.push('Direct LLM: not invoked');
    }
    if (obs.reason_label) lines.push(`Reason: ${obs.reason_label}`);
    else if (obs.reason) lines.push(`Reason: ${obs.reason}`);
    return lines;
  }
  if (!detail) return [];
  return [detail];
}

/**
 * True when a thinking step should not be truncated to a single short line.
 */
export function thinkingStepNeedsExpandedDetail(step = {}) {
  if (step && step.observability && typeof step.observability === 'object') {
    return true;
  }
  return String(step.detail || '').includes('\n');
}

/**
 * Contract helpers for tests: never treat planned direct_llm as enterprise fallback.
 */
export function isEnterpriseDirectLlmStep(step = {}) {
  const obs = step && step.observability;
  if (obs && obs.kind === 'summary_gated_direct_llm') return false;
  if (obs && obs.kind === 'enterprise_direct_llm_fallback') return true;
  return String(step.label || '').startsWith('Enterprise Direct LLM fallback');
}

export function isSummaryGatedDirectLlmStep(step = {}) {
  const obs = step && step.observability;
  if (obs && obs.kind === 'summary_gated_direct_llm') return true;
  return String(step.label || '').startsWith('Direct LLM (summary-gated)');
}

export function deepTaskClaimsEnterpriseDirectLlm(step = {}) {
  const obs = step && step.observability;
  if (!obs || obs.kind !== 'deep_task') return false;
  // Assigned/planned direct_llm must never be presented as enterprise fallback execution.
  return false;
}
